import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { getProfile, getToken, clearStorage } from 'utilities';
import { routePaths } from './routesConfig';
import { fireStoreDB, auth } from 'services';

const PrivateRoute = ({ children }) => {
  const [authState, setAuthState] = useState('loading'); // 'loading' | 'authenticated' | 'unauthenticated'
  const [isBusinessUser, setIsBusinessUser] = useState(null);
  const location = useLocation();

  useEffect(() => {
    // Use Firebase's onAuthStateChanged for proper token validation
    // This verifies the token is valid and not expired (not just checking localStorage)
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        // No valid Firebase session — token is invalid or expired (e.g. after a
        // password change on another device, or normal expiry). Clear the stale
        // local token so the sign-in page doesn't bounce us back here in a loop.
        clearStorage();
        setAuthState('unauthenticated');
        return;
      }

      // User is authenticated with a valid Firebase token
      try {
        const profile = getProfile();
        const uid = profile?.uid || user.uid;

        // Check if user is a business user
        const q = query(
          collection(fireStoreDB, 'businesses'),
          where('userId', '==', uid)
        );
        const snapshot = await getDocs(q);
        setIsBusinessUser(!snapshot.empty);
      } catch (err) {
        console.error('Error verifying business user:', err);
        setIsBusinessUser(false);
      }

      setAuthState('authenticated');
    });

    return () => unsubscribe();
  }, []);

  // Show loading while Firebase verifies the token
  if (authState === 'loading' || (authState === 'authenticated' && isBusinessUser === null)) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Loading...</div>
      </div>
    );
  }

  // Not authenticated — redirect to sign in
  if (authState === 'unauthenticated') {
    return <Navigate to={routePaths.signIn} replace />;
  }

  // Business user — redirect to /business
  if (isBusinessUser) {
    if (location.pathname !== routePaths.business) {
      return <Navigate to={routePaths.business} replace />;
    }
    return children;
  }

  return children;
};

export default PrivateRoute;
